import { Document, Font, Page, pdf, StyleSheet, Text, View } from "@react-pdf/renderer"
import { BalanceSheetReportType } from "pages/Accounts/Report/ProfitAndLoss/types"
import { useHandleAuth } from "utils"
import { buildHierarchy } from "../functions"
import moment from "moment"
import { CoaEnum } from "pages/Accounts/Report/ProfitAndLoss/enums"
import { useGestSingleSettingCompany } from "pages/Setup/Preference/SettingCompany/utils"

type UseExportPdf = {
  data: BalanceSheetReportType[]
  comparisonDates: string[]
}
type HandleExportPdfType = {
  date?: string
  endDate?: string
  displayAccountNumber?: boolean
}

export const useExportPdf = ({ data, comparisonDates }: UseExportPdf) => {
  const dataSettingCompany = useGestSingleSettingCompany()
  const mapData: BalanceSheetReportType[] = []
  const auth = useHandleAuth()
  const token = auth.userTokenData()

  const dataWithOutNetProfit = data.filter((item) => item.coa_name?.toUpperCase() !== CoaEnum.NET_PROFIT)

  const parents = dataWithOutNetProfit?.filter((item) => item.parent_id === -1)
  const mapDataHirarki = parents?.map((item) => {
    const hierarchy = buildHierarchy(dataWithOutNetProfit, item.coa_id)
    return { ...item, children: hierarchy }
  })

  makeRowsPdf(mapDataHirarki, mapData)

  const income: number[] = dataWithOutNetProfit?.find((d) => d.coa_name?.toUpperCase() === CoaEnum.INCOME)?.balance ?? []
  const cogs: number[] = dataWithOutNetProfit?.find((d) => d.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES)?.balance ?? []
  const expenses: number[] = dataWithOutNetProfit?.find((d) => d.coa_name?.toUpperCase() === CoaEnum.EXPENSES)?.balance ?? []

  const grossProfit: number[] = income.map((val, idx) => (val || 0) - (cogs[idx] || 0))
  const ebit: number[] = grossProfit.map((gp, idx) => gp - (expenses[idx] ?? 0))
  const tax: number[] = ebit.map((val) => (val || 0) * (dataSettingCompany?.data?.[0]?.tax ?? 0))
  const netProfit: number[] = ebit.map((val, idx) => val - (tax[idx] ?? 0))

  mapData.push({
    coa_id: 999999,
    parent_id: -1,
    coa_name: "NET PROFIT",
    balance: netProfit,
    coa_level: 1,
    header_level: 1,
    bold: true
  } as BalanceSheetReportType)

  return {
    handleExportPdf: ({ date, endDate, displayAccountNumber }: HandleExportPdfType) =>
    pdf(
      <TableProfitLoss
        companyName={token?.company_name ?? ""}
        data={mapData}
        comparisonDates={comparisonDates}
        date={date}
        endDate={endDate}
        displayAccountNumber={displayAccountNumber}
      />
    )
    .toBlob()
    .then((blob) => {
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "profit_loss.pdf"
      link.click()
      URL.revokeObjectURL(url)
    })
    .catch((error) => console.error("Gagal membuat PDF:", error))
  }
}

export const makeRowsPdf = (data: BalanceSheetReportType[], mapData: BalanceSheetReportType[]) => {
  data.forEach((item) => {
    let itemRow = { ...item }

    if (["NET ASSETS", "NET PROFIT"].includes(item.coa_name) || item.header_level !== 3) itemRow.bold = true

    mapData.push(itemRow)

    if (item?.children!?.length > 0) makeRowsPdf(item?.children!, mapData)

    if (item.header_level !== 3 && !["NET ASSETS", "NET PROFIT"].includes(item.coa_name)) {
      mapData.push({ ...item, coa_name: `Total ${item.coa_name}`, bold: true })
    }
  })
}

type TableProfitLossProps = {
  companyName: string
  data: BalanceSheetReportType[]
  comparisonDates: string[]
  endDate?: string
  date?: string
  displayAccountNumber?: boolean
}

export const TableProfitLoss = ({ companyName, data, comparisonDates, endDate, date, displayAccountNumber }: TableProfitLossProps) => {
  Font.register({
    family: "Roboto",
    fonts: [
      { src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf" },
      { src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf", fontWeight: "medium" },
      { src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf", fontWeight: "bold" }
    ]
  })

  const styles = StyleSheet.create({
    table: { display: "flex", width: "auto" },
    tableRow: { flexDirection: "row" },
    tableCol: { flex: 1 },
    tableCell: { margin: 2, fontSize: 8, fontFamily: "Roboto" },
    headerText: { margin: 2, fontSize: 9, fontWeight: "bold", fontFamily: "Roboto" }
  })

  const income = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.INCOME)?.balance ?? []
  const cogs = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES)?.balance ?? []
  const operatingExpenses = data.find((d: any) => d.coa_name?.toUpperCase() === CoaEnum.OPERATING_EXPENSES)?.balance ?? []

  const grossProfit = income.map((val: number, idx: number) => (val || 0) - (cogs[idx] || 0))
  const ebitda = grossProfit.map((gp: number, idx: number) => gp - (operatingExpenses[idx] ?? 0))

  return (
    <Document>
      <Page size="A4" style={{ paddingHorizontal: 15, paddingVertical: 20 }}>
        <Text style={{ 
          fontSize: 12, 
          marginBottom: 12, 
          paddingTop: 6,
          paddingBottom: 6,
          textAlign: "center", 
          fontWeight: "bold",
          backgroundColor: "black",
          color: "white"
        }}>
          PROFIT AND LOSS
        </Text>
        <Text style={{ fontSize: 12, marginBottom: 4, textAlign: "left", fontWeight: "bold" }}>
          {companyName?.toUpperCase() ?? ""}
        </Text>
        <Text style={{ fontSize: 8, marginBottom: 8 }}>
          From {moment(date).format("DD MMM YYYY")} to {moment(endDate).format("DD MMM YYYY")}
        </Text>

        <View style={[{ borderBottomWidth: 0.5 }]} />
        
        <View style={styles.table}>
          <View style={[styles.tableRow, { borderBottomWidth: 0.5 }]}>
            <View style={styles.tableCol} />
            {comparisonDates.map((col, index) => (
              <View key={index} style={styles.tableCol}>
                <Text style={[styles.headerText, { textAlign: "right", fontSize: 7 }]}>
                  {moment(col, ["MMM YYYY", "DD/MM/YYYY - DD/MM/YYYY"]).format("DD MMM YYYY")}
                </Text>
              </View>
            ))}
          </View>

          {data.map((item, rowIndex) => {
            const rows: JSX.Element[] = []

            rows.push(
              <View wrap={false} key={`row-${rowIndex}`} style={[styles.tableRow, { borderBottomWidth: 0.5 }]}>
                <View style={[styles.tableCol, { paddingLeft: (item.coa_level + 1) * 5 }]}>
                  <Text style={[styles.tableCell, { fontWeight: item.bold ? "bold" : "normal" }]}>
                    {displayAccountNumber ? item.coacode + " " : ""}{item.coa_name}
                  </Text>
                </View>
                {item.balance?.map((balance: number, idx: number) => (
                  <View key={`bal-${rowIndex}-${idx}`} style={styles.tableCol}>
                    <Text style={[styles.tableCell, { textAlign: "right" }]}>
                      {balance > 0
                        ? balance.toLocaleString("id-ID")
                        : balance < 0
                          ? `(${Math.abs(balance).toLocaleString("id-ID")})`
                          : "-"}
                    </Text>
                  </View>
                ))}
              </View>
            )

            if (item.coa_name?.toUpperCase() === CoaEnum.COST_OF_SALES) {
              rows.push(
                <View key="gross-profit" style={[styles.tableRow, { backgroundColor: "#000" }]}>
                  <View style={styles.tableCol}>
                    <Text style={[styles.tableCell, { color: "#fff", fontWeight: "bold" }]}>
                      GROSS PROFIT
                    </Text>
                  </View>
                  {grossProfit.map((gp: any, idx: any) => (
                    <View key={`gp-${idx}`} style={styles.tableCol}>
                      <Text style={[styles.tableCell, { textAlign: "right", color: "#fff", fontWeight: "bold" }]}>
                        {gp.toLocaleString("id-ID")}
                      </Text>
                    </View>
                  ))}
                </View>
              )
            }

            if (item.coa_name?.toUpperCase() === CoaEnum.EXPENSES) {
              rows.push(
                <View key="ebitda" style={[styles.tableRow, { backgroundColor: "#000" }]}>
                  <View style={styles.tableCol}>
                    <Text style={[styles.tableCell, { color: "#fff", fontWeight: "bold" }]}>
                      EBITDA
                    </Text>
                  </View>
                  {ebitda.map((val: any, idx: any) => (
                    <View key={`ebitda-${idx}`} style={styles.tableCol}>
                      <Text style={[styles.tableCell, { textAlign: "right", color: "#fff", fontWeight: "bold" }]}>
                        {val.toLocaleString("id-ID")}
                      </Text>
                    </View>
                  ))}
                </View>
              )
            }

            return rows
          })}
        </View>
      </Page>
    </Document>
  )
}